<template>
  <b-modal
    :active="true"
    :can-cancel="['escape', 'x', 'outside']"
    @close="onClose"
    width="80%"
  >
    <div class="content">
      <div class="header">
        <template v-if="!instrumentation">
          <h1 class="title">Add New Instrument</h1>
          <div style="display: flex; gap: 10px">
            <b-upload v-model="file" class="file-label">
              <span class="file-cta">
                <b-icon class="file-icon" icon="upload"></b-icon>
                <span class="file-label">Import</span>
              </span>
            </b-upload>
            <b-button
              type="is-primary"
              icon-left="download"
              @click="onDownloadTemplate"
              >Download Template File</b-button
            >
          </div>
        </template>

        <template v-if="instrumentation">
          <h1 class="title">Edit Instrument</h1>
        </template>
      </div>
      <div class="columns">
        <div class="column">
          <b-field label="Instrument ID*">
            <b-input v-model="instrumentId" required></b-input>
          </b-field>
        </div>

        <div class="column">
          <b-field label="Name*">
            <b-input v-model="instrumentName" required></b-input>
          </b-field>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <b-field label="Type*" v-if="mteTypes && selectedType">
            <b-dropdown
              v-model="selectedType"
              :active.sync="isTypeDropdownActive"
              aria-role="list"
              :max-height="200"
              scrollable
            >
              <template #trigger>
                <b-input
                  v-model="typeSearch"
                  placeholder="Search type..."
                  icon="magnify"
                  @focus="isTypeDropdownActive = true"
                  @input="isTypeDropdownActive = true"
                />
              </template>

              <b-dropdown-item
                v-for="type in filteredMteTypes"
                :key="type._id"
                :value="type"
                @click="onSelectType(type)"
              >
                {{ type.name }}
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </div>
        <div class="column">
          <b-field label="Status*">
            <b-dropdown
              aria-role="list"
              v-model="selectedStatus"
              :max-height="200"
              scrollable
            >
              <template #trigger>
                <b-button
                  :label="selectedStatus.title"
                  icon-right="menu-down"
                />
              </template>

              <b-dropdown-item
                :value="status"
                aria-role="listitem"
                v-for="status in INSTRUMENTATION_STATUS"
                :key="status.value"
              >
                {{ status.title }}
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <b-field label="Last Calibration*">
            <b-datepicker
              placeholder="Select a date..."
              icon="calendar-today"
              v-model="lastCalibrationDate"
              position="is-top-right"
            >
            </b-datepicker>
          </b-field>
        </div>

        <div class="column is-one-fifth">
          <b-field label="Calibration Schedule*">
            <b-dropdown
              aria-role="list"
              v-model="selectedPeriod"
              :max-height="200"
              scrollable
              @change="onPeriodChange"
            >
              <template #trigger>
                <b-button
                  :label="selectedPeriod.title"
                  icon-right="menu-down"
                />
              </template>

              <b-dropdown-item
                :value="period"
                aria-role="listitem"
                v-for="period in INSTRUMENTATION_PERIODS"
                :key="period.value"
              >
                {{ period.title }}
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </div>

        <div class="column">
          <b-field label="Calibration Due Date*">
            <b-datepicker
              placeholder="Select a date..."
              icon="calendar-today"
              v-model="dueDate"
              position="is-top-right"
              :editable="false"
              :min-date="lastCalibrationDate"
              :disabled="selectedPeriod.value !== 'manual'"
            >
            </b-datepicker>
          </b-field>
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <b-field label="Storage Location">
            <b-input v-model="storageLocation"></b-input>
          </b-field>
        </div>
        <div class="column">
          <b-field label="Responsible">
            <b-input v-model="responsible"></b-input>
          </b-field>
        </div>
      </div>

      <footer class="footer-buttons">
        <b-button label="Cancel" @click="onClose" type="is-danger" />
        <b-button
          v-if="!instrumentation"
          label="Save"
          @click="onSave"
          type="is-success"
        />
        <b-button
          v-if="instrumentation"
          label="Update"
          @click="onUpdate"
          type="is-primary"
        />
      </footer>
    </div>
  </b-modal>
</template>

<script>
import * as xlsx from 'xlsx';

import { INSTRUMENTATION_PERIODS, INSTRUMENTATION_STATUS } from './constants';

export default {
  name: 'add-instrument-modal',

  props: {
    instrumentation: {
      type: Object,
      default: null,
    },
  },

  computed: {
    sites() {
      return this.$store.state.sites;
    },

    mteTypes() {
      return this.$store.state.mteTypes;
    },

    filteredMteTypes() {
      if (!this.typeSearch) return this.mteTypes;
      return this.mteTypes.filter((type) =>
        type.name.toLowerCase().includes(this.typeSearch.toLowerCase())
      );
    },
  },

  async mounted() {
    if (!this.mteTypes || this.mteTypes.length === 0)
      await this.$store.dispatch('getMteTypes');
    if (this.instrumentation) {
      this.instrumentId = this.instrumentation.instrumentId;
      this.instrumentName = this.instrumentation.instrumentName;
      this.selectedType = this.mteTypes.find(
        (type) => type._id === this.instrumentation.typeId
      );

      this.typeSearch = this.selectedType ? this.selectedType.name : '';
      this.dueDate = this.instrumentation.calibrationDueDate
        ? new Date(this.instrumentation.calibrationDueDate)
        : null;
      this.lastCalibrationDate = this.instrumentation.lastCalibrationDate
        ? new Date(this.instrumentation.lastCalibrationDate)
        : null;

      this.selectedPeriod =
        INSTRUMENTATION_PERIODS.find(
          (period) =>
            period.value ===
            parseInt(this.instrumentation.calibrationSchedule, 10)
        ) || INSTRUMENTATION_PERIODS[0];

      this.selectedStatus =
        INSTRUMENTATION_STATUS.find(
          (status) => status.value === this.instrumentation.status
        ) || INSTRUMENTATION_STATUS[0];
      this.storageLocation = this.instrumentation.storageLocation;
      this.responsible = this.instrumentation.responsible;
    } else {
      // eslint-disable-next-line prefer-destructuring
      this.selectedType = this.mteTypes[0];
    }
  },

  data() {
    return {
      instrumentId: '',
      instrumentName: '',
      selectedType: null,
      dueDate: null,
      lastCalibrationDate: null,
      file: null,
      INSTRUMENTATION_PERIODS,
      INSTRUMENTATION_STATUS,
      selectedPeriod: INSTRUMENTATION_PERIODS[0],
      selectedStatus: INSTRUMENTATION_STATUS[0],
      storageLocation: '',
      responsible: '',
      typeSearch: '',
      isTypeDropdownActive: false,
    };
  },

  watch: {
    file(newFile) {
      if (newFile) {
        this.parseExcelFile();
      }
    },
  },

  methods: {
    onSelectType(type) {
      this.selectedType = type;
      this.typeSearch = type.name;
      this.isTypeDropdownActive = false;
    },

    isValidSelectedType() {
      return (
        this.selectedType &&
        this.selectedType._id &&
        this.typeSearch === this.selectedType.name
      );
    },

    onPeriodChange(newPeriod) {
      if (newPeriod.value !== 'manual' && this.lastCalibrationDate) {
        const lastDate = new Date(this.lastCalibrationDate);
        let dueDate = null;
        dueDate = new Date(
          lastDate.getFullYear(),
          lastDate.getMonth() + newPeriod.value,
          lastDate.getDate()
        );

        this.dueDate = dueDate;
      }
    },
    onDownloadTemplate() {
      this.$emit('downloadTemplate');
    },

    onClose() {
      this.$emit('close');
    },

    onSave() {
      if (!this.checkRequiredFields()) return;
      this.$emit('onSave', {
        instrumentId: this.instrumentId,
        instrumentName: this.instrumentName,
        typeName: this.selectedType.name,
        typeId: this.selectedType._id,
        calibrationDueDate: this.dueDate,
        lastCalibrationDate: this.lastCalibrationDate,
        calibrationSchedule: this.selectedPeriod.value,
        status: this.selectedStatus.value,
        storageLocation: this.storageLocation,
        responsible: this.responsible,
      });
    },

    onUpdate() {
      if (!this.checkRequiredFields()) return;
      this.$emit('onUpdate', {
        _id: this.instrumentation._id,
        instrumentId: this.instrumentId,
        instrumentName: this.instrumentName,
        typeName: this.selectedType.name,
        typeId: this.selectedType._id,
        calibrationDueDate: this.dueDate,
        lastCalibrationDate: this.lastCalibrationDate,
        calibrationSchedule: this.selectedPeriod.value,
        status: this.selectedStatus.value,
        storageLocation: this.storageLocation,
        responsible: this.responsible,
      });
    },

    checkRequiredFields() {
      const validation =
        this.instrumentId &&
        this.instrumentId.length > 0 &&
        this.instrumentName &&
        this.instrumentName.length > 0 &&
        this.isValidSelectedType() &&
        this.dueDate instanceof Date &&
        this.lastCalibrationDate instanceof Date;

      if (!validation) {
        this.$buefy.toast.open({
          message: 'Please fill in all required fields.',
          type: 'is-danger',
          duration: 5000,
        });
        return false;
      }
      return true;
    },

    async parseExcelFile() {
      if (!this.file) return;
      try {
        const fileReader = new FileReader();
        fileReader.onload = async (event) => {
          const data = new Uint8Array(event.target.result);
          const workbook = xlsx.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = xlsx.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length > 0) {
            const headers = jsonData[0];

            const requiredHeaders = [
              'Instrument ID',
              'Name',
              'Type Name',
              'Status',
              'Last Calibration Date',
              'Calibration Schedule (months)',
              'Calibration Due Date',
              'Storage Location',
              'Responsible',
            ];

            const missingHeaders = requiredHeaders.filter(
              (header) => !headers.includes(header)
            );

            if (missingHeaders.length > 0) {
              this.$buefy.toast.open({
                message: `The file is missing the following headers: ${missingHeaders.join(
                  ', '
                )}`,
                type: 'is-danger',
                duration: 5000,
              });
              return;
            }

            const rows = jsonData.slice(1).map((row) => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index];
              });
              return obj;
            });

            const rowsWithAtleasOneValue = rows.filter((row) =>
              requiredHeaders.some(
                (header) => row[header] && String(row[header]).trim()
              )
            );

            this.$emit('fileParsed', rowsWithAtleasOneValue);
          } else {
            this.$buefy.toast.open({
              message: 'The file is empty or invalid.',
              type: 'is-danger',
              duration: 5000,
            });
          }
        };

        fileReader.readAsArrayBuffer(this.file);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Error parsing the file.',
          type: 'is-danger',
          duration: 5000,
        });
        console.error(error);
      } finally {
        this.file = undefined;
      }
    },
  },
};
</script>
<style scoped>
.content {
  background: white;
  border-radius: 10px;
  padding: 20px;
  gap: 10px;
  display: flex;
  flex-direction: column;
  min-height: 650px;
  justify-content: space-between;
}

.header {
  display: flex;
  justify-content: space-between;
}

.footer-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}

::v-deep .dropdown,
::v-deep .dropdown-trigger,
::v-deep .dropdown-trigger .button {
  width: 100%;
}

::v-deep .dropdown-trigger .button {
  display: flex;
  justify-content: space-between;
}

::v-deep .dropdown-menu {
  width: 100%;
}

::v-deep .dropdown-item {
  width: 100%;
}

::v-deep .dropdown-item > a {
  display: block;
  width: 100%;
}
</style>
